import type { CarFilters, CarFilterType } from '@/scopes/checkout/filterableCars/domain/types'
import type { FilterMappers } from '@/scopes/checkout/filterableCars/domain/types/filterMappers'
import type { GetAllCarsResponseMapper } from '@/scopes/checkout/filterableCars/infrastructure/api/getAllCarsResponseMapper'

import type { APIFilterableCars } from '@/scopes/checkout/filterableCars/infrastructure/api/types'
import type { BookingDraftParameters } from '../../../domain/types'
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import defaultFetch from 'isomorphic-unfetch'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  getAllCarsResponseMapper: GetAllCarsResponseMapper
  queryStringSerializer: QueryStringSerializer
  filtersMappers: FilterMappers
}

export function createFilterableCarsAPIClient(dependencies: Partial<Dependencies> & Pick<Dependencies, 'getAllCarsResponseMapper'>) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
    getAllCarsResponseMapper,
    filtersMappers,
  } = dependencies as Dependencies

  async function getAllCars(apiContext: BookingDraftParameters) {
    const url = buildGetAllCarsEndpointUrl(apiContext)
    const response = await fetch(url, { credentials: 'include' })

    const responseData: APIFilterableCars = await response.json()

    return getAllCarsResponseMapper.map(responseData)
  }

  async function setCarFilterSelection<K extends CarFilterType>(
    apiContext: BookingDraftParameters,
    filterType: K,
    updatedFilter: CarFilters[K],
  ) {
    const filterMapper = filtersMappers[filterType]
    const filterSelection = filterMapper.mapSelection(updatedFilter)
    const url = buildSetCarFilterSelectionEndpointUrl(apiContext)
    await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: filterType,
        data: filterSelection,
      }),
    })
  }

  async function setSelectFilteredCar(apiContext: BookingDraftParameters, carCode: string) {
    const url = buildSetSelectFilteredCarEndpointUrl(apiContext)
    await fetch(url, {
      method: 'PATCH',
      body: JSON.stringify({
        selectedCardCode: carCode,
      }),
    })
  }

  function buildGetAllCarsEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/FilterableCars/GetAllCars?${queryString}`
  }

  function buildSetCarFilterSelectionEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/FilterableCars/ApplyCarFilter?${queryString}`
  }

  function buildSetSelectFilteredCarEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/FilterableCars/SelectFilteredCar?${queryString}`
  }

  return {
    getAllCars,
    setCarFilterSelection,
    setSelectFilteredCar,
  }
}
